import {
  adminConnectorSchema,
  adminLeadLabelSchema,
  adminLeadLabelsSchema,
  adminLeadLabelSuggestionsSchema,
  adminLeadRunDeletePreviewSchema,
  adminLeadRunSchema,
  adminLeadRunsSchema,
  adminLeadSchema,
  adminLeadsDeleteAllResultSchema,
  adminLeadsSchema,
  type AdminConnector,
  type AdminLead,
  type AdminLeadCreate,
  type AdminLeadLabel,
  type AdminLeadLabelChange,
  type AdminLeadLabelCreate,
  type AdminLeadLabelSuggestion,
  type AdminLeadLabelUpdate,
  type AdminLeadRun,
  type AdminLeadRunDeletePreview,
  type AdminLeadsDeleteAll,
  type AdminLeadsDeleteAllResult,
  type AdminLeadsQuery,
  type AdminLeadUpdate,
} from "@teamna/shared";
import { z } from "zod";
import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { ccApi } from "./cc-api.ts";

/**
 * The Lead Finder hooks of Control Center: leads, labels, runs, suggestions, start over and the
 * connector. Every write reads every Lead Finder query again: a label score moves every lead, and
 * a label on a lead moves the label counts, so a narrower refresh would show old numbers.
 */

const ROOT = ["cc", "admin", "lead-finder"] as const;

export const leadKeys = {
  root: ROOT,
  leads: (query: AdminLeadsQuery) => [...ROOT, "leads", query] as const,
  labels: [...ROOT, "labels"] as const,
  runs: [...ROOT, "runs"] as const,
  runDeletePreview: (runId: string) => [...ROOT, "runs", runId, "delete-preview"] as const,
  suggestions: [...ROOT, "suggestions"] as const,
  connector: [...ROOT, "connector"] as const,
};

function queryString(query: AdminLeadsQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value) params.set(key, String(value));
  const text = params.toString();
  return text ? `?${text}` : "";
}

function useRefresh(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ROOT });
}

export function useLeads(query: AdminLeadsQuery): UseQueryResult<AdminLead[]> {
  return useQuery({
    queryKey: leadKeys.leads(query),
    queryFn: async () => (await ccApi.get(`/admin/leads${queryString(query)}`, adminLeadsSchema)).items,
    staleTime: 15 * 1000,
  });
}

export function useLeadLabels(): UseQueryResult<AdminLeadLabel[]> {
  return useQuery({
    queryKey: leadKeys.labels,
    queryFn: async () => (await ccApi.get("/admin/lead-labels", adminLeadLabelsSchema)).items,
    staleTime: 30 * 1000,
  });
}

export function useLeadRuns(): UseQueryResult<AdminLeadRun[]> {
  return useQuery({
    queryKey: leadKeys.runs,
    queryFn: async () => (await ccApi.get("/admin/lead-runs", adminLeadRunsSchema)).items,
    staleTime: 15 * 1000,
  });
}

export function useLeadRunDeletePreview(runId: string | null): UseQueryResult<AdminLeadRunDeletePreview> {
  return useQuery({
    queryKey: leadKeys.runDeletePreview(runId ?? ""),
    queryFn: () => ccApi.get(`/admin/lead-runs/${runId}/delete-preview`, adminLeadRunDeletePreviewSchema),
    enabled: runId !== null,
    staleTime: 0,
  });
}

export function useLeadSuggestions(): UseQueryResult<AdminLeadLabelSuggestion[]> {
  return useQuery({
    queryKey: leadKeys.suggestions,
    queryFn: async () => (await ccApi.get("/admin/lead-label-suggestions", adminLeadLabelSuggestionsSchema)).items,
    staleTime: 15 * 1000,
  });
}

export function useConnector(): UseQueryResult<AdminConnector> {
  return useQuery({
    queryKey: leadKeys.connector,
    queryFn: () => ccApi.get("/admin/connector", adminConnectorSchema),
    staleTime: 30 * 1000,
  });
}

// --- writes -----------------------------------------------------------------------------------

export function useCreateLead(): UseMutationResult<AdminLead, Error, AdminLeadCreate> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (input) => ccApi.post("/admin/leads", input, adminLeadSchema), onSuccess: refresh });
}

export function useUpdateLead(): UseMutationResult<AdminLead, Error, { leadId: string; changes: AdminLeadUpdate }> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: ({ leadId, changes }) => ccApi.patch(`/admin/leads/${leadId}`, changes, adminLeadSchema), onSuccess: refresh });
}

export function useChangeLeadLabel(): UseMutationResult<AdminLead, Error, { leadId: string; change: AdminLeadLabelChange }> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: ({ leadId, change }) => ccApi.post(`/admin/leads/${leadId}/labels`, change, adminLeadSchema), onSuccess: refresh });
}

export function useDeleteLead(): UseMutationResult<void, Error, string> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (leadId) => ccApi.delete(`/admin/leads/${leadId}`), onSuccess: refresh });
}

export function useCreateLeadLabel(): UseMutationResult<AdminLeadLabel, Error, AdminLeadLabelCreate> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (input) => ccApi.post("/admin/lead-labels", input, adminLeadLabelSchema), onSuccess: refresh });
}

export function useUpdateLeadLabel(): UseMutationResult<AdminLeadLabel, Error, { labelId: string; changes: AdminLeadLabelUpdate }> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: ({ labelId, changes }) => ccApi.patch(`/admin/lead-labels/${labelId}`, changes, adminLeadLabelSchema), onSuccess: refresh });
}

export function useDeleteLeadLabel(): UseMutationResult<void, Error, string> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (labelId) => ccApi.delete(`/admin/lead-labels/${labelId}`), onSuccess: refresh });
}

export function useAddLeadSuggestion(): UseMutationResult<AdminLeadLabel, Error, { suggestionId: string; changes?: AdminLeadLabelUpdate }> {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({ suggestionId, changes }) => ccApi.post(`/admin/lead-label-suggestions/${suggestionId}/add`, changes ?? {}, adminLeadLabelSchema),
    onSuccess: refresh,
  });
}

export function useDismissLeadSuggestion(): UseMutationResult<unknown, Error, string> {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (suggestionId) => ccApi.post(`/admin/lead-label-suggestions/${suggestionId}/dismiss`, {}, z.object({ dismissed: z.literal(true) })),
    onSuccess: refresh,
  });
}

export function useSaveLeadRunNote(): UseMutationResult<AdminLeadRun, Error, { runId: string; note: string | null }> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: ({ runId, note }) => ccApi.patch(`/admin/lead-runs/${runId}`, { note }, adminLeadRunSchema), onSuccess: refresh });
}

export function useDeleteLeadRun(): UseMutationResult<void, Error, string> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (runId) => ccApi.delete(`/admin/lead-runs/${runId}`), onSuccess: refresh });
}

export function useDeleteAllLeads(): UseMutationResult<AdminLeadsDeleteAllResult, Error, AdminLeadsDeleteAll> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (input) => ccApi.post("/admin/leads/delete-all", input, adminLeadsDeleteAllResultSchema), onSuccess: refresh });
}

export function useRevokeConnectorGrant(): UseMutationResult<void, Error, string> {
  const refresh = useRefresh();
  return useMutation({ mutationFn: (grantId) => ccApi.delete(`/admin/connector/grants/${grantId}`), onSuccess: refresh });
}
